import {
  optimisticallySendMessage,
  useUIMessages,
} from "@convex-dev/agent/react";
/**
 * Plasma AI Chat Component
 *
 * Main chat interface following Convex agent patterns.
 * Uses useUIMessages for reactive message streaming.
 *
 * V4: Includes context-aware suggestions based on current page.
 * V4.1: LLM-generated dynamic suggestions (replaces static suggestions)
 */
import { api } from "@convex/_generated/api";
import { useMutation, useQuery } from "convex/react";

// Avoids TS2589 (type instantiation is excessively deep) from api.d.ts chain
const abortCurrentStreamRef = api.ai.threads.abortCurrentStream;

const getPendingActionForThreadRef = api.ai.queries.getPendingActionForThread;

// Typed as compatible with UIMessagesQuery + StreamQuery so stream: true and
// optimisticallySendMessage both typecheck without touching the deep api.d.ts chain
const listMessagesRef = api.ai.threads.listMessages;

const sendMessageRef = api.ai.threads.sendMessage;

import { cn } from "@plasma/utils/cn";
import {
  AlertCircle,
  History,
  RotateCcw,
  SendHorizontal,
  StopCircle,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "~/components/ui/button";
import { ScrollArea } from "~/components/ui/scroll-area";
import { Spinner } from "~/components/ui/spinner";
import { logger } from "~/lib/logger";

import { AIPreferencesToggle } from "./ai-preferences-toggle";
import { MessageAreaContent } from "./chat-message-area";
import { FocusIndicator } from "./focus-indicator";
import { useLLMSuggestions } from "./hooks/use-agent-context";
import { useAIProgress } from "./hooks/use-ai-progress";
import { usePlasmaThread } from "./hooks/use-plasma-thread";
import { useRateLimitStatus } from "./hooks/use-rate-limit";
import { useWriteConfirmation } from "./hooks/use-write-confirmation";
import { ThreadList } from "./thread-list";
import { WriteConfirmationDialog } from "./write-confirmation-dialog";

interface PlasmaAIChatProps {
  /** Optional class name for the container */
  className?: string;
  /** Called when user wants to close the chat */
  onClose?: () => void;
}

export function PlasmaAIChat({ className, onClose }: PlasmaAIChatProps) {
  const {
    threadId,
    createThread,
    isReady,
    threadDetails,
    locationFocus,
    setLocationFocus,
    organizationInfo,
    userId,
    startNewThread,
    selectThread,
  } = usePlasmaThread();

  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // V2.5: Rate limiting and progress tracking
  const rateLimit = useRateLimitStatus();
  const progress = useAIProgress(threadId);
  const abortStreamMutation = useMutation(abortCurrentStreamRef);

  // V4.1: LLM-generated dynamic suggestions
  const { suggestions, isLoading: isSuggestionsLoading } = useLLMSuggestions();

  // Write confirmation flow
  const {
    pendingAction,
    showConfirmation,
    hideConfirmation,
    confirm,
    reject,
    isProcessing: isConfirming,
    isOpen: isConfirmDialogOpen,
  } = useWriteConfirmation();

  // Fetch messages with streaming support
  const {
    results: messages,
    status,
    loadMore,
  } = useUIMessages(listMessagesRef, threadId ? { threadId } : "skip", {
    initialNumItems: 50,
    stream: true,
  });

  // Mutation with optimistic update for instant feedback
  // This is the pattern from Convex agent examples
  const sendMessageMutation = useMutation(sendMessageRef).withOptimisticUpdate(
    optimisticallySendMessage(listMessagesRef)
  );

  // Subscribe to pending actions for this thread
  const pendingActionData = useQuery(
    getPendingActionForThreadRef,
    threadId ? { threadId } : "skip"
  );

  // Auto-scroll to bottom when new messages arrive
  const messageCount = messages?.length;
  useEffect(() => {
    // Only scroll if there are messages
    if (messageCount) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messageCount]);

  // Show confirmation dialog when a pending action exists
  useEffect(() => {
    if (pendingActionData && !isConfirmDialogOpen) {
      showConfirmation({
        pendingActionId: pendingActionData.pendingActionId,
        description: pendingActionData.description,
        affectedEntity: pendingActionData.affectedEntity,
        changes: pendingActionData.changes,
        expiresAt: pendingActionData.expiresAt,
        toolName: pendingActionData.toolName,
      });
    }
  }, [pendingActionData, showConfirmation, isConfirmDialogOpen]);

  // V2.5: Handle abort stream
  const handleAbort = async () => {
    if (!threadId) {
      return;
    }
    try {
      await abortStreamMutation({ threadId });
    } catch (error) {
      logger.exception("ai_stream_abort_failed", error);
    }
  };

  const handleSend = async () => {
    if (!(input.trim() && isReady) || isSending) {
      return;
    }
    if (!(userId && organizationInfo?.id)) {
      return;
    }
    // V2.5: Check rate limit before sending
    if (!rateLimit.ok) {
      return;
    }

    const prompt = input.trim();
    setInput("");
    setIsSending(true);

    try {
      if (threadId) {
        // Send to existing thread with optimistic update
        await sendMessageMutation({
          threadId,
          prompt,
          organizationId: organizationInfo.id,
          userId,
          locationId: locationFocus,
        });
      } else {
        // Create new thread first, then send message with optimistic update
        const newThreadId = await createThread();
        selectThread(newThreadId);
        // Now send the first message (will trigger optimistic update)
        await sendMessageMutation({
          threadId: newThreadId,
          prompt,
          organizationId: organizationInfo.id,
          userId,
          locationId: locationFocus,
        });
      }
    } catch (error) {
      // Restore input on error
      setInput(prompt);
      logger.exception("ai_message_send_failed", error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  // V4: Handle suggestion click - populate input field
  const handleSuggestionClick = (question: string) => {
    setInput(question);
  };

  const isStreaming = messages?.some((m) => m.status === "streaming");

  return (
    <div className={cn("flex h-full flex-col", className)}>
      {/* Header with focus indicator */}
      <div className="bg-background/95 supports-[backdrop-filter]:bg-background/60 flex items-center justify-between border-b p-3 backdrop-blur">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">Plasma AI</span>
          <FocusIndicator
            currentLocationId={locationFocus}
            isOrgWide={!locationFocus}
            locationName={threadDetails?.locationName ?? undefined}
            onClearFocus={() => setLocationFocus(undefined)}
            onSelectLocation={(id) => setLocationFocus(id)}
            organizationName={organizationInfo?.name}
          />
        </div>
        <div className="flex items-center gap-1">
          <AIPreferencesToggle compact />
          <Button
            className={showHistory ? "bg-accent" : ""}
            onClick={() => setShowHistory(!showHistory)}
            size="icon"
            title={showHistory ? "Back to chat" : "Conversation history"}
            type="button"
            variant="ghost"
          >
            <History className="h-4 w-4" />
          </Button>
          <Button
            onClick={async () => {
              startNewThread();
              setShowHistory(false);
            }}
            size="icon"
            title="New conversation"
            type="button"
            variant="ghost"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
          {onClose && (
            <Button
              onClick={onClose}
              size="icon"
              title="Close"
              type="button"
              variant="ghost"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Content area - either history or messages */}
      {showHistory ? (
        <ThreadList
          className="flex-1"
          onNewThread={() => {
            startNewThread();
            setShowHistory(false);
          }}
          onSelectThread={(id) => {
            selectThread(id);
            setShowHistory(false);
          }}
          selectedThreadId={threadId}
        />
      ) : (
        <ScrollArea className="flex-1 p-4">
          <MessageAreaContent
            handleSuggestionClick={handleSuggestionClick}
            isReady={isReady}
            isSuggestionsLoading={isSuggestionsLoading}
            loadMore={loadMore}
            messages={messages}
            messagesEndRef={messagesEndRef}
            status={status}
            suggestions={suggestions}
          />
        </ScrollArea>
      )}

      {/* Input area */}
      <div className="bg-background border-t p-3">
        {/* V2.5: Rate limit indicator */}
        {!rateLimit.ok && rateLimit.retryInSeconds !== null && (
          <div className="text-destructive mb-2 flex items-center gap-2 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>
              {rateLimit.limitType === "tokens"
                ? `Token limit reached. Please wait ${rateLimit.retryInSeconds} seconds.`
                : `Rate limited. Please wait ${rateLimit.retryInSeconds} seconds.`}
            </span>
          </div>
        )}

        {/* V2.5: Progress indicator */}
        {progress.isTracking && (
          <div className="text-muted-foreground mb-2 flex items-center gap-2 text-xs">
            <Spinner className="h-3 w-3" />
            <span>
              Step {progress.step}
              {progress.totalSteps ? `/${progress.totalSteps}` : ""}
              {progress.completedTools.length > 0 && (
                <> · {progress.completedTools.at(-1)}</>
              )}
            </span>
          </div>
        )}

        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSend();
          }}
        >
          <textarea
            className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring max-h-[120px] min-h-[40px] flex-1 resize-none rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            disabled={!isReady || isSending || !rateLimit.ok}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={rateLimit.ok ? "Ask Plasma AI..." : "Please wait..."}
            rows={1}
            value={input}
          />
          {isStreaming ? (
            <Button
              onClick={handleAbort}
              size="icon"
              title="Stop generation"
              type="button"
              variant="destructive"
            >
              <StopCircle className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              disabled={
                !(input.trim() && isReady) || isSending || !rateLimit.ok
              }
              size="icon"
              type="submit"
            >
              {isSending ? (
                <Spinner className="h-4 w-4" />
              ) : (
                <SendHorizontal className="h-4 w-4" />
              )}
            </Button>
          )}
        </form>
      </div>

      {/* Write Confirmation Dialog */}
      <WriteConfirmationDialog
        isOpen={isConfirmDialogOpen}
        isProcessing={isConfirming}
        onClose={hideConfirmation}
        onConfirm={confirm}
        onReject={reject}
        pendingAction={pendingAction}
      />
    </div>
  );
}
